import FieldError from 'components/field-error/field-error';
import FormField from 'components/form-field/form-field';
import SelectTradeField from 'components/select-trade-field/select-trade-field';
import { useFormikContext } from 'formik';
import {
  useProViewRouteQueryLazyQuery,
  useTradeFieldsSuggestionsQuery,
} from 'generated/graphql';
import { ProFormUpdateFields } from 'pages/pros/pro-form/pro-form-update-fields-type';
import React, { useCallback, useMemo, useState } from 'react';
import { FunctionComponent } from 'react';
import { useParams } from 'react-router';
import { isNumber } from 'remeda';
import {
  Button,
  Divider,
  DropdownItemProps,
  Form,
  Icon,
  Label as LabelUi,
  Modal,
  Popup,
} from 'semantic-ui-react';

type TradeFieldsProps = {
  bilikZoneId: number;
  action: 'create-pro' | 'update-pro' | 'add-zone';
};

const TradeFields: FunctionComponent<TradeFieldsProps> = ({
  bilikZoneId,
  action,
}) => {
  const proViewId = Number(useParams<{ proViewId: string }>().proViewId);

  const { values, setFieldTouched, setFieldValue, errors } =
    useFormikContext<ProFormUpdateFields>();

  const [getOtherProViews, { data }] = useProViewRouteQueryLazyQuery({
    variables: { proViewId },
  });

  const getOtherProViewsData = useCallback(() => {
    getOtherProViews();
  }, [getOtherProViews]);

  const proPresentationProViewsBilikZone = useMemo(
    () =>
      data?.proViewByPk?.proPresentation.proViews.map(
        (proView) => proView.bilikZone.mainCityCode,
      ),
    [data],
  );

  const [selectMainTradeOptions, setSelectMainTradeOptions] = useState<
    DropdownItemProps[]
  >([]);

  const [updateMainTradesModalOpen, setUpdateMainTradesModalOpen] =
    useState<boolean>(false);

  const [temporaryMainTradeIds, setTemporaryMainTradeIds] = useState<number[]>(
    values.proPresentation.mainTrades,
  );

  const { data: matchingTradeGuides } = useTradeFieldsSuggestionsQuery({
    variables: {
      tradeIds: values.trades,
      mainTradeIds: values.proPresentation.mainTrades,
    },
  });

  const tradeGuideSuggestions = useMemo(
    () =>
      matchingTradeGuides?.possibleTradeGuides?.filter(
        (tradeGuide) => tradeGuide.trades.length >= 2,
      ) ?? [],
    [matchingTradeGuides],
  );

  const activeTradeGuides = useMemo(
    () =>
      matchingTradeGuides?.possibleTradeGuides
        ?.filter((tradeGuide) =>
          tradeGuide.trades.every(({ trade }) =>
            values.proPresentation.mainTrades.includes(trade.id),
          ),
        )
        // Trade guides with more trades is prioritized
        .sort((a, b) => b.trades.length - a.trades.length) ?? [],
    [matchingTradeGuides, values.proPresentation.mainTrades],
  );

  return (
    <>
      <SelectTradeField
        // Force render when mainTrades change
        key={`select-trade-field-${values.proPresentation.mainTrades.join(
          '-',
        )}`}
        label="Catégories"
        name="trades"
        bilikZoneId={bilikZoneId}
        multiple
        selection
        search
        required
        onOptionsLoad={(options): void => {
          setSelectMainTradeOptions(
            options.filter((option) =>
              values.trades.includes(Number(option.key)),
            ),
          );
        }}
        renderLabel={
          // We can't use this renderLabel function when we create a pro
          // because we don't have the trades in the formik values yet
          action !== 'create-pro'
            ? (item: DropdownItemProps) => {
                return (
                  <LabelUi
                    color={
                      isNumber(item.value) &&
                      values.proPresentation.mainTrades.includes(item.value)
                        ? 'blue'
                        : undefined
                    }
                    content={item.text}
                    removeIcon={
                      isNumber(item.value) &&
                      !values.proPresentation.mainTrades.includes(item.value)
                        ? 'delete'
                        : null
                    }
                    onRemove={(_, data) => {
                      setFieldTouched('trades', true);
                      setFieldValue(
                        'trades',
                        values.trades.filter((trade) => trade !== data.value),
                      );
                    }}
                  />
                );
              }
            : undefined
        }
        onChange={async (_, { value, options }): Promise<void> => {
          const newValues = value as number[];

          if (
            !newValues.some((tradeId) =>
              values.proPresentation.mainTrades.includes(tradeId),
            )
          ) {
            setFieldTouched('proPresentation.mainTrades', true);
            setFieldValue('proPresentation.mainTrades', []);
          }

          setFieldTouched('trades', true);
          setFieldValue('trades', newValues);

          if (options) {
            setSelectMainTradeOptions(
              options.filter((option) => newValues.includes(option.key)),
            );
          }
        }}
      />
      <Form.Group widths="equal">
        <FormField
          search
          required
          label="Catégorie(s) principale(s)"
          type="select"
          selection
          name="proPresentation.mainTrades"
          multiple
          placeholder="Sélectionnez une/des catégorie(s)..."
          options={selectMainTradeOptions}
          onChange={
            action !== 'create-pro'
              ? (_, { value }): void => {
                  // If not a number (empty string or null), set to null
                  setTemporaryMainTradeIds(value as number[]);
                  getOtherProViewsData();
                  setUpdateMainTradesModalOpen(true);
                }
              : undefined
          }
          disabled={action === 'add-zone'}
        />
      </Form.Group>

      {values.proPresentation.mainTrades.length > 1 &&
      errors['proPresentation']?.['mainTrades'] ? (
        <>
          <FieldError color="red">
            Cette association de catégories principales n&apos;est pas
            autorisée. Tu peux consulter{' '}
            <a href="/trades_guide" target="_blank" rel="noopener noreferrer">
              les associations de catégories spécifiques au guide
            </a>{' '}
            pour voir les associations autorisées.
          </FieldError>
          <Divider hidden />
        </>
      ) : null}

      <p>
        <b>Guide</b> :{' '}
        {activeTradeGuides?.length ? (
          <LabelUi color="grey" horizontal>
            {activeTradeGuides[0].name}
          </LabelUi>
        ) : null}
        {!activeTradeGuides?.length ? (
          <>
            {matchingTradeGuides?.mainTrades.map(({ name }, index) => (
              <LabelUi
                key={name}
                color={index === 0 ? 'grey' : 'red'}
                horizontal
              >
                {name}
              </LabelUi>
            ))}
          </>
        ) : null}
      </p>

      {tradeGuideSuggestions.length > 0 && (
        <p>
          <u>Suggestions de catégories principales possibles pour le guide :</u>
          <ul>
            {tradeGuideSuggestions.map((tradeGuide) => (
              <Popup
                key={tradeGuide.name}
                trigger={
                  <li style={{ cursor: 'pointer' }} key={tradeGuide.name}>
                    {tradeGuide.name}
                  </li>
                }
                content={tradeGuide.trades
                  .map(({ trade }) => trade.name)
                  .join(' + ')}
              />
            ))}
          </ul>
        </p>
      )}
      <Modal open={updateMainTradesModalOpen} size="tiny">
        <Modal.Header>
          <Icon name="warning sign" color="red" />
          Attention
        </Modal.Header>
        <Modal.Content>
          <p>
            Pensez à <b>enregistrer</b> pour mettre à jour la/les catégorie(s)
            principale(s).{' '}
            {proPresentationProViewsBilikZone &&
            proPresentationProViewsBilikZone?.length > 1 ? (
              <>
                <b>
                  Pour info, la/les catégorie(s) principale(s) seront également
                  modifiées dans les zones{' '}
                </b>
                :
                <ul>
                  {proPresentationProViewsBilikZone?.map((mainCityCode) => (
                    <li key={mainCityCode}>{mainCityCode}</li>
                  ))}
                </ul>
              </>
            ) : null}
          </p>

          <p>Êtes-vous sûr de vouloir continuer ?</p>
        </Modal.Content>
        <Modal.Actions>
          <Button
            onClick={(): void => {
              setUpdateMainTradesModalOpen(false);
            }}
          >
            Annuler
          </Button>
          <Button
            primary
            onClick={(): void => {
              setFieldValue(
                'proPresentation.mainTrades',
                temporaryMainTradeIds,
              );
              setUpdateMainTradesModalOpen(false);
            }}
          >
            Confirmer
          </Button>
        </Modal.Actions>
      </Modal>
    </>
  );
};

export default TradeFields;
